<template>
  <div>
    <h1 class="structure-header">
      {{ $t('SEARCH.TITLE') }} | MUZIK AIR
    </h1>
    <div ref="search" class="search" @scroll="checkOnScrolling">
      <div ref="searchContainer" class="search-container">
        <div ref="searchHeader" class="search-header">
          <Logo :text-in-mobile="true" />
          <a class="cancel" @click="back">
            <i class="icon material-icons">
              close
            </i>
          </a>
        </div>
        <!--  -->
        <div class="search-body fade-in">
          <SearchRead v-if="$production.toUpperCase() === 'READ'" />
          <SearchListen v-if="$production.toUpperCase() === 'LISTEN'" />
          <SearchConcert v-if="$production.toUpperCase() === 'CONCERT'" />
          <SearchCSS v-if="$production.toUpperCase() === 'AIR'" />
        </div>
        <!--  -->
        <div class="search-footer">
          <div v-show="getSearchStatus === 'PENDING' || getSearchStatus === 'DEBUNCE'" class="search-skeleton-wrapper">
            <SearchSkeleton />
          </div>
        </div>
      </div>
      <div v-if="!getSearchedKeyword">
        <Paganiniplus />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Logo from '@/components/common/Logo'
import Paganiniplus from '@/components/tsp/Paganiniplus'
import SearchRead from '@/components/search/SearchRead'
import SearchListen from '@/components/search/SearchListen'
import SearchConcert from '@/components/search/SearchConcert'
import SearchCSS from '@/components/search/SearchCSS'
import SearchSkeleton from '@/components/search/SearchSkeleton'

export default {
  name: 'Search',
  components: {
    Logo,
    Paganiniplus,
    SearchSkeleton,
    SearchRead,
    SearchListen,
    SearchConcert,
    SearchCSS
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.previousPageName = from.name
    })
  },
  props: {},
  data() {
    return {
      previousPageName: undefined,
      searchScrollTop: 0,
      searchClientWidth: 0,
      debounceTimer: 0
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        ...this.$route.meta.metaInfo.meta,
        { vmid: 'name', name: 'name', content: this.title },
        { vmid: 'description', name: 'description', content: this.description },
        { vmid: 'keywords', name: 'keywords', content: this.keywords },
        { vmid: 'og:title', name: 'og:title', content: this.title },
        { vmid: 'og:description', name: 'og:description', content: this.description },
        { vmid: 'og:url', name: 'og:url', content: this.canonicalURL }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: this.canonicalURL },
        { vmid: 'image_src', rel: 'image_src', href: this.cover }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'getSearchType',
      'getSearchStatus',
      'getSearchResults',
      'getSearchPage',
      'getSearchedKeyword'
    ]),
    title() {
      const productionLocaleName = this.$t(`${this.$production.toUpperCase()}.TITLE`)
      return this.getSearchedKeyword
        ? `${this.$t('SEARCH.RESULT', { keyword: this.getSearchedKeyword })} | ${productionLocaleName} | MUZIK AIR`
        : `${this.$t('SEARCH.TITLE')} | ${productionLocaleName} | MUZIK AIR`
    },
    description() {
      return this.title
    },
    keywords() {
      return this.$t('SEARCH.TITLE')
    },
    cover() {
      const subDomainKey = `VUE_APP_MUZIK_${this.$production.toUpperCase()}`
      const isAirProduction = (this.$production === 'AIR') &&
                              (process.env['VUE_APP_MUZIK_STAGE'] === 'production')
      const coverPath = this.$production.toLowerCase()
      return isAirProduction
        ? `https://${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/${coverPath}.png`
        : `https://${process.env[subDomainKey]}.${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/${coverPath}.png`
    },
    canonicalURL() {
      const subDomainKey = `VUE_APP_MUZIK_${this.$production.toUpperCase()}`
      const isAirProduction = (this.$production === 'AIR') &&
                              (process.env['VUE_APP_MUZIK_STAGE'] === 'production')
      if (isAirProduction && this.getSearchedKeyword) {
        return `https://${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/search?q=${this.getSearchedKeyword}`
      } else if (isAirProduction && this.getSearchedKeyword) {
        return `https://${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/search`
      } else if (this.getSearchedKeyword) {
        return `https://${process.env[subDomainKey]}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/search?q=${this.getSearchedKeyword}`
      } else {
        return `https://${process.env[subDomainKey]}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/search`
      }
      // return this.getSearchedKeyword
      //   ? `https://${process.env[subDomainKey]}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/search?q=${this.getSearchedKeyword}`
      //   : `https://${process.env[subDomainKey]}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/search`
    }
  },
  watch: {},
  created() {},
  mounted() {
    this.$nextTick(() => {
      this.updateInputPosition()
    })
  },
  updated() {},
  methods: {
    back() {
      if (!this.previousPageName) {
        this.$router.push({ name: 'index', params: { locale: this.$i18n.localePath() } })
      } else {
        this.$router.go(-1)
      }
    },
    updateInputPosition() {
      let inputPosition = 'relative'
      if (this.searchClientWidth >= 1440) {
        inputPosition = this.searchScrollTop < 128 ? 'relative' : 'fixed'
      } else if (this.searchClientWidth >= 1024) {
        inputPosition = this.searchScrollTop < 128 ? 'relative' : 'fixed'
      } else if (this.searchClientWidth >= 768) {
        inputPosition = this.searchScrollTop < 64 ? 'relative' : 'fixed'
      } else {
        inputPosition = this.searchScrollTop < 64 ? 'relative' : 'fixed'
      }
      this.$store.commit('UPDATE_INPUT_POSITION', inputPosition)
    },
    checkOnScrolling() {
      if (this.$refs.search) {
        this.searchScrollTop = this.$refs.search.scrollTop
        this.searchClientWidth = this.$refs.search.clientWidth
        this.updateInputPosition()
        //
        const searchScrollTop = this.$refs.search.scrollTop
        const searchScrollHeight = this.$refs.search.scrollHeight
        const searchClientHeight = this.$refs.search.clientHeight
        const scrollPercentage = searchScrollTop / (searchScrollHeight - searchClientHeight)
        if (scrollPercentage && scrollPercentage >= 0.9) {
          if (this.getSearchPage.total > this.getSearchPage.current) {
            this.debouncedLoadMore()
            // this.loadMore()
          }
        } else {
          // No more...
        }
      }
    },
    debouncedLoadMore() {
      this.$store.commit('UPDATE_STATUS', 'DEBUNCE')
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
        // this.debounceTimer = 0
      }
      this.debounceTimer = setTimeout(() => {
        this.loadMore()
        this.debounceTimer = 0
      }, 300)
    },
    loadMore() {
      const params = {
        page: Number.parseInt(this.getSearchPage.current) + 1
      }
      if (this.$production.toUpperCase() === 'LISTEN' || this.$production.toUpperCase() === 'AIR') {
        params.lang = this.$i18n.locale.toLowerCase()
      }
      this.$store.dispatch('KEYWORD_ON_SUBMIT_MORE', params)
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
.structure-header {
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  visibility: hidden;
}
.search-skeleton-wrapper {
  margin: 48px 0;
}
.search {
  z-index: 90;
  position: fixed;
  width: 100%;
  height: 100vh;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: white;
  // border: solid 1px #a10903;
  overflow-x: hidden;
  overflow-y: scroll;
  &-container {
    max-width: 1440px;
    margin: 0 auto 300px;
    padding: 0 $padding-lg-grid-x1;
  }
  &-body {
    animation: fade-in .4s 0s 1 linear;
  }
  &-header {
    // border: solid 1px #a10903;
    .cancel {
      float: right;
      cursor: pointer;
      .icon {
        font-size: 36px;
        color: $grey-7d;
        line-height: 64px;
        vertical-align: middle;
        &:hover {
          color: $link-hover;
        }
      }
    }
  }
  &-footer {
    margin: 48px 0;
    // border: solid 1px #a10903;
  }
}
@media only screen and (min-width: 1440px) {}
@media only screen and (min-width: 1024px) and (max-width: 1439.98px) {
  .search {
    &-container {
      padding: 0 $padding-md-grid-x1;
    }
    &-body {}
    &-footer {}
  }
}
@media only screen and (min-width: 768px) and (max-width: 1023.98px) {
  .search-skeleton-wrapper {
    margin: 24px 0;
  }
  .search {
    &-container {
      padding: 0 $padding-sm-grid-x1;
    }
    &-body {}
    &-header {
      .cancel {
        .icon {
          font-size: 24px;
        }
      }
    }
    &-footer {}
  }
}
@media only screen and (max-width: 767.98px) {
  .search-skeleton-wrapper {
    margin: 24px 0;
  }
  .search {
    &-container {
      padding: 0 $padding-xs-grid-x1;
    }
    &-body {}
    &-header {
      .cancel {
        .icon {
          font-size: 24px;
        }
      }
    }
    &-footer {}
  }
}
</style>
